/**
 * V250: 증명서 발급 관련 CF 호출 Hook
 *
 * - 직원: 신청, 내 목록 조회, 다운로드
 * - HR: 요청 목록 조회, 승인/반려, 템플릿 CRUD
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';

// ===== 타입 정의 =====

export interface CertificateRequest {
  id: string;
  requesterId: string;
  requesterName: string;
  companyCode: string;
  type: string;
  purpose: string;
  language: string;
  copies?: number;
  status: 'pending' | 'approved' | 'rejected' | 'pdf_failed';
  requestedAt: string | null;
  reviewedAt?: string | null;
  reviewedBy?: string;
  rejectReason?: string;
  issueNumber?: string;
  pdfPath?: string;
  pdfFileName?: string;
  issuedAt?: string | null;
  templateId?: string;
}

export interface CertificateTemplate {
  id: string;
  companyCode: string;
  type: string;
  version: number;
  isActive: boolean;
  title: string;
  htmlTemplate: string;
  placeholders: string[];
  sealImagePath: string;
  signatureImagePath?: string;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy: string;
  updatedBy: string;
}

// ===== Hook =====

export function useCertificates() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const functions = getFunctions();

  // ---- 직원 함수 ----

  const createRequest = useCallback(
    async (input: { type: string; purpose: string; language?: string }) => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<typeof input, { success: boolean; requestId: string }>(
          functions,
          'createCertificateRequest'
        );
        const result = await fn(input);
        return result.data;
      } catch (err: any) {
        const msg = err?.message || '증명서 신청 중 오류가 발생했습니다.';
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  const listMyRequests = useCallback(
    async (input?: { year?: number; status?: string }) => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<typeof input, { success: boolean; items: CertificateRequest[] }>(
          functions,
          'listMyCertificateRequests'
        );
        const result = await fn(input || {});
        return result.data.items;
      } catch (err: any) {
        const msg = err?.message || '증명서 목록 조회 중 오류가 발생했습니다.';
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  const getDownloadUrl = useCallback(
    async (requestId: string) => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<{ requestId: string }, { success: boolean; downloadUrl: string; fileName: string }>(
          functions,
          'getCertificateDownloadUrl'
        );
        const result = await fn({ requestId });
        return result.data;
      } catch (err: any) {
        const msg = err?.message || '다운로드 URL 발급 중 오류가 발생했습니다.';
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  // ---- HR 함수 ----

  const listRequestsForHr = useCallback(
    async (input?: { companyCode?: string; status?: string; year?: number; query?: string; page?: number; pageSize?: number; lastDocId?: string }) => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<
          typeof input,
          { success: boolean; items: CertificateRequest[]; total: number; hasMore: boolean; nextCursor?: string }
        >(functions, 'listCertificateRequestsForHr');
        const result = await fn(input || {});
        return result.data;
      } catch (err: any) {
        const msg = err?.message || '증명서 요청 목록 조회 중 오류가 발생했습니다.';
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  const approveRequest = useCallback(
    async (requestId: string) => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<{ requestId: string }, { success: boolean; issueNumber: string }>(
          functions,
          'approveCertificateRequest'
        );
        const result = await fn({ requestId });
        return result.data;
      } catch (err: any) {
        const msg = err?.message || '증명서 승인 중 오류가 발생했습니다.';
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  const rejectRequest = useCallback(
    async (requestId: string, reason: string) => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<{ requestId: string; reason: string }, { success: boolean }>(
          functions,
          'rejectCertificateRequest'
        );
        const result = await fn({ requestId, reason });
        return result.data;
      } catch (err: any) {
        const msg = err?.message || '증명서 반려 중 오류가 발생했습니다.';
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  // ---- 템플릿 함수 ----

  const listTemplates = useCallback(
    async (input?: { companyCode?: string; type?: string; activeOnly?: boolean }) => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<typeof input, { success: boolean; templates: CertificateTemplate[] }>(
          functions,
          'listCertificateTemplates'
        );
        const result = await fn(input || {});
        return result.data.templates;
      } catch (err: any) {
        const msg = err?.message || '템플릿 목록 조회 중 오류가 발생했습니다.';
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  const createTemplate = useCallback(
    async (input: {
      companyCode?: string;
      type: string;
      title: string;
      htmlTemplate: string;
      placeholders?: string[];
      sealImagePath: string;
      signatureImagePath?: string;
    }) => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<typeof input, { success: boolean; templateId: string }>(
          functions,
          'createCertificateTemplate'
        );
        const result = await fn(input);
        return result.data;
      } catch (err: any) {
        const msg = err?.message || '템플릿 생성 중 오류가 발생했습니다.';
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  const updateTemplate = useCallback(
    async (input: {
      templateId: string;
      title?: string;
      htmlTemplate?: string;
      placeholders?: string[];
      isActive?: boolean;
      sealImagePath?: string;
      signatureImagePath?: string;
    }) => {
      try {
        setIsLoading(true);
        setError(null);
        const fn = httpsCallable<typeof input, { success: boolean }>(
          functions,
          'updateCertificateTemplate'
        );
        const result = await fn(input);
        return result.data;
      } catch (err: any) {
        const msg = err?.message || '템플릿 수정 중 오류가 발생했습니다.';
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  return {
    isLoading,
    error,
    // 직원
    createRequest,
    listMyRequests,
    getDownloadUrl,
    // HR
    listRequestsForHr,
    approveRequest,
    rejectRequest,
    // 템플릿
    listTemplates,
    createTemplate,
    updateTemplate,
  };
}
